'use client';

import { Box, HStack, VStack, token } from '@terra/ui-styled-system';
import { MetaDefault } from '@terra/ui-design-system';
import { CrownIcon, Cancel01Icon, Radar01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import type { InternalNetworkAttackPathEventKind } from '@terra/shared-contracts-internal-network';
import {
  ATTACK_PATH_CHAIN_STATUS_OUTLINE_COLOR,
  ATTACK_PATH_EVENT_VISUALS,
  ATTACK_PATH_FAILED_EVENT_COLOR,
  translucentColor,
} from './attack-path-event-visuals';
import { GRAPH_NODE_COLOR, GRAPH_NODE_ICON } from './constants';

const AD_KIND_ORDER = ['USER', 'GROUP', 'COMPUTER', 'DOMAIN'] as const;

const KIND_ORDER: InternalNetworkAttackPathEventKind[] = [
  'HOST_DISCOVERED',
  'SERVICE_DISCOVERED',
  'WEAKNESS_EXPLOITED',
  'CREDENTIAL_OBTAINED',
  'DATA_ACCESSED',
  'IMPACT_ACHIEVED',
];

type InternalNetworkAttackPathLegendProps = {
  showOwnedSeed?: boolean;
  showAdEntityKinds?: boolean;
  showEventKinds?: boolean;
  showUnknownGap?: boolean;
  showRunning?: boolean;
  showAborted?: boolean;
};

export function InternalNetworkAttackPathLegend({
  showOwnedSeed,
  showAdEntityKinds,
  showEventKinds = true,
  showUnknownGap,
  showRunning,
  showAborted,
}: InternalNetworkAttackPathLegendProps) {
  const tKinds = useTranslations('internalNetwork.attackPathEventKinds');
  const tGraph = useTranslations('internalNetwork.attackPathGraph');
  const tOverview = useTranslations('internalNetwork.overview');
  const tExecutions = useTranslations('internalNetwork.executions');
  const tEntityKinds = useTranslations(
    'internalNetwork.crownJewels.entityKinds',
  );

  return (
    <VStack
      gap={2}
      alignItems="flex-start"
      p={3}
      borderRadius="md"
      borderWidth="1px"
      borderColor="border.subtle"
      bg="bg.default/80"
      style={{ backdropFilter: 'blur(2px)' }}
    >
      {showEventKinds ? (
        <HStack gap={3} flexWrap="wrap">
          {KIND_ORDER.map((kind) => {
            const visual = ATTACK_PATH_EVENT_VISUALS[kind];
            const Icon = visual.icon;

            return (
              <HStack key={kind} gap={1.5}>
                <Box
                  w="18px"
                  h="18px"
                  borderRadius="sm"
                  borderWidth="1px"
                  borderStyle="solid"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  style={{
                    backgroundColor: translucentColor({
                      color: visual.color,
                      percent: 16,
                    }),
                    borderColor: translucentColor({
                      color: visual.color,
                      percent: 45,
                    }),
                    color: visual.color,
                  }}
                >
                  <Icon size={10} strokeWidth={2.5} />
                </Box>
                <MetaDefault color="fg.muted">{tKinds(kind)}</MetaDefault>
              </HStack>
            );
          })}
        </HStack>
      ) : null}
      {showAdEntityKinds ? (
        <HStack gap={3} flexWrap="wrap">
          {AD_KIND_ORDER.map((kind) => {
            const Icon = GRAPH_NODE_ICON[kind];
            const color = GRAPH_NODE_COLOR[kind];

            return (
              <HStack key={kind} gap={1.5}>
                <Box
                  w="18px"
                  h="18px"
                  borderRadius="sm"
                  borderWidth="1px"
                  borderStyle="solid"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  style={{
                    backgroundColor: translucentColor({
                      color,
                      percent: 16,
                    }),
                    borderColor: translucentColor({
                      color,
                      percent: 45,
                    }),
                    color,
                  }}
                >
                  <Icon size={10} strokeWidth={2.5} />
                </Box>
                <MetaDefault color="fg.muted">{tEntityKinds(kind)}</MetaDefault>
              </HStack>
            );
          })}
        </HStack>
      ) : null}
      <HStack gap={3} flexWrap="wrap">
        <HStack gap={1.5}>
          <Box
            px={1.5}
            py={0.5}
            borderRadius="sm"
            bg="bg.success.subtle"
            display="flex"
            alignItems="center"
          >
            <Radar01Icon size={10} />
          </Box>
          <MetaDefault color="fg.muted">{tOverview('provenPath')}</MetaDefault>
        </HStack>
        <HStack gap={1.5}>
          <Box
            w="18px"
            h="18px"
            borderRadius="full"
            display="flex"
            alignItems="center"
            justifyContent="center"
            backgroundColor="orange.9"
          >
            <CrownIcon size={10} color="white" strokeWidth={2.5} />
          </Box>
          <MetaDefault color="fg.muted">{tOverview('crownJewel')}</MetaDefault>
        </HStack>
        <HStack gap={1.5}>
          <Box
            w="18px"
            h="12px"
            borderRadius="sm"
            borderWidth="1px"
            borderColor={ATTACK_PATH_CHAIN_STATUS_OUTLINE_COLOR.COMPLETED}
          />
          <MetaDefault color="fg.muted">
            {tExecutions('status.COMPLETED')}
          </MetaDefault>
        </HStack>
        <HStack gap={1.5}>
          <Box
            w="18px"
            h="12px"
            borderRadius="sm"
            borderWidth="1px"
            borderColor={ATTACK_PATH_CHAIN_STATUS_OUTLINE_COLOR.IN_PROGRESS}
          />
          <MetaDefault color="fg.muted">
            {tExecutions('status.RUNNING')}
          </MetaDefault>
        </HStack>
        <HStack gap={1.5}>
          <Box
            w="18px"
            h="12px"
            borderRadius="sm"
            borderWidth="1px"
            borderColor={ATTACK_PATH_CHAIN_STATUS_OUTLINE_COLOR.BLOCKED}
          />
          <MetaDefault color="fg.muted">
            {tExecutions('status.BLOCKED')}
          </MetaDefault>
        </HStack>
        <HStack gap={1.5}>
          <Box
            w="18px"
            h="18px"
            borderRadius="full"
            display="flex"
            alignItems="center"
            justifyContent="center"
            style={{ backgroundColor: ATTACK_PATH_FAILED_EVENT_COLOR }}
          >
            <Cancel01Icon size={10} color="white" strokeWidth={2.5} />
          </Box>
          <MetaDefault color="fg.muted">{tGraph('failed')}</MetaDefault>
        </HStack>
        {showOwnedSeed ? (
          <HStack gap={1.5}>
            <Box
              w="18px"
              h="12px"
              borderRadius="sm"
              borderWidth="1px"
              borderColor="fg.success"
              bg="bg.success.subtle"
            />
            <MetaDefault color="fg.muted">{tGraph('ownedSeed')}</MetaDefault>
          </HStack>
        ) : null}
        {showUnknownGap ? (
          <HStack gap={1.5}>
            <Box
              w="18px"
              h="18px"
              borderRadius="sm"
              bg="bg.muted"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <MetaDefault color="fg.muted">?</MetaDefault>
            </Box>
            <MetaDefault color="fg.muted">{tGraph('unknownGap')}</MetaDefault>
          </HStack>
        ) : null}
        {showRunning ? (
          <HStack gap={1.5}>
            <Box
              w="18px"
              h="12px"
              borderRadius="sm"
              style={{
                boxShadow: `0 0 0 2px ${token('colors.border.accent.default')}`,
              }}
            />
            <MetaDefault color="fg.muted">{tGraph('running')}</MetaDefault>
          </HStack>
        ) : null}
        {showAborted ? (
          <HStack gap={1.5}>
            <Box
              w="18px"
              h="18px"
              borderRadius="sm"
              borderWidth="1px"
              borderStyle="dashed"
              style={{
                borderColor: translucentColor({
                  color: token('colors.fg.error'),
                  percent: 45,
                }),
                backgroundColor: translucentColor({
                  color: token('colors.fg.error'),
                  percent: 3,
                }),
              }}
            />
            <MetaDefault color="fg.muted">{tGraph('aborted')}</MetaDefault>
          </HStack>
        ) : null}
      </HStack>
    </VStack>
  );
}
